import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  HStack,
  Heading,
  Box,
  Image,
  Button,
  VStack,
  Switch,
  Text,
  Flex,
  Input,
} from '@chakra-ui/react';
import Select from 'react-select';
import { Controller, useForm } from 'react-hook-form';

import {
  useGetAllRolesQuery,
  useGetTeamMembersByProjectIdQuery,
  useGetTeamMembersByPhaseIdQuery,
} from '../../../../services';
import { useTypedSelector } from '../../../../hooks';
import ReceiverNodeIcon from '../../../../assets/projects/receiver_node.svg';
import { ReceivedNodeData } from '../../models';

export const ReceiverNodeEditorModal = ({
  isOpen,
  onClose,
  receivedNodeData: {
    receivedRoles,
    receivedUsers,
    hasRejection,
    durationUnit,
    durationValue,
  },
  phaseId,
  projectId,
}: {
  isOpen: boolean;
  onClose: (data?: ReceivedNodeData) => void;
  receivedNodeData: ReceivedNodeData;
  phaseId?: string;
  projectId: string;
}) => {
  const [usersOptions, setUsersOptions] = useState<
    {
      firstName: string;
      id: number;
    }[]
  >([]);
  const [rolesOptions, setRolesOptions] = useState<
    {
      name: string;
      id: number;
    }[]
  >([]);
  const { userDetails } = useTypedSelector(state => state.auth);

  const { handleSubmit, control, register } = useForm<ReceivedNodeData>({
    defaultValues: {
      receivedRoles,
      receivedUsers,
      hasRejection,
      durationUnit,
      durationValue,
    },
  });

  const { data: allUsersByProject } = useGetTeamMembersByProjectIdQuery(
    {
      page: 0,
      size: 1000,
      projectId,
    },
    {
      skip: Boolean(phaseId),
    }
  );

  const { data: allUsersByPhase } = useGetTeamMembersByPhaseIdQuery(
    {
      page: 0,
      size: 1000,
      phaseId: String(phaseId),
      projectId,
      name: '',
    },
    {
      skip: !Boolean(phaseId),
    }
  );

  const { data: allRoles } = useGetAllRolesQuery(
    {
      basicInfoId: Number(userDetails?.companyBasicInfo?.id),
      page: 0,
      size: 10000,
    },
    {
      skip: !userDetails?.companyBasicInfo?.id,
    }
  );

  useEffect(() => {
    if (allRoles?.data) {
      setRolesOptions(
        allRoles.data.content.map(({ id, name }) => ({
          name,
          id,
        }))
      );
    }

    if (allUsersByProject?.data || allUsersByPhase?.data.content) {
      setUsersOptions(
        (allUsersByProject?.data || allUsersByPhase?.data.content)?.map(
          ({ id, firstName }) => ({
            firstName,
            id,
          })
        ) || []
      );
    }
  }, [allRoles?.data, allUsersByProject?.data, allUsersByProject?.data]);

  const onSubmit = (data: ReceivedNodeData) => {
    onClose(data);
  };

  return (
    <Modal
      blockScrollOnMount={false}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
    >
      <ModalOverlay />

      <ModalContent pb="4" borderRadius="md">
        <ModalHeader bg="custom_gray.5" borderTopRadius="md">
          <HStack>
            <Image src={ReceiverNodeIcon} />
            <Heading fontSize="md" fontWeight="medium" color="white">
              Receiver Node
            </Heading>
          </HStack>
          <ModalCloseButton color="white" />
        </ModalHeader>
        <ModalBody>
          <HStack sx={{ mt: 4, mb: 2 }}>
            <Text fontWeight={'bold'}>Who is going to receive it?</Text>
          </HStack>
          <VStack
            as="form"
            align="stretch"
            gap="2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box>
              <Controller
                control={control}
                name="receivedRoles"
                render={({ field }) => (
                  <Select
                    isMulti
                    {...field}
                    options={rolesOptions}
                    getOptionValue={({ id }) => String(id)}
                    getOptionLabel={({ name }) => (
                      <Box
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        ml="1.5"
                        maxW="40"
                      >
                        {name}
                      </Box>
                    )}
                  />
                )}
              />
            </Box>

            <Box>
              <Controller
                control={control}
                name="receivedUsers"
                render={({ field }) => (
                  <Select
                    isMulti
                    {...field}
                    options={usersOptions}
                    getOptionValue={({ id }) => String(id)}
                    getOptionLabel={({ firstName }) => (
                      <Box
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        ml="1.5"
                        maxW="40"
                      >
                        {firstName}
                      </Box>
                    )}
                  />
                )}
              />
            </Box>

            <HStack sx={{ mt: 4, mb: 2 }}>
              <Text fontWeight={'bold'}>Estimated time for this step</Text>
            </HStack>

            <Flex gap="4px" sx={{ mb: 4 }}>
              <Box flex={1}>
                <Controller
                  control={control}
                  name="durationValue"
                  render={({ field }) => (
                    <Input {...field} type="number" placeholder="Example: 3" />
                  )}
                />
              </Box>
              <Box flex={1}>
                <Controller
                  control={control}
                  name="durationUnit"
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={
                        [
                          { label: 'Days', value: 'Day' },
                          { label: 'Hours', value: 'Hour' },
                        ] as {
                          label: string;
                          value: string;
                        }[]
                      }
                      getOptionValue={option => option.value}
                      getOptionLabel={option => (
                        <Box
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          ml="1.5"
                          maxW="40"
                        >
                          {option.label}
                        </Box>
                      )}
                    />
                  )}
                />
              </Box>
            </Flex>

            <HStack sx={{ mb: 2 }}>
              <Text fontWeight={'bold'}>Can the assignee reject the task?</Text>
            </HStack>
            <HStack>
              <Switch colorScheme="orange" {...register('hasRejection')} />
              <Text>Rejected</Text>
            </HStack>

            <HStack>
              <Button
                bg="custom_orange.2"
                _hover={{ bg: 'custom_orange.2' }}
                color="white"
                w="full"
                type="submit"
                mt="2"
              >
                Save
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
